import React, { FormEvent, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FiLock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { AdminLayout, Breadcrumb } from '@grcs/components';
import { useUserOperations } from '@grcs/hook/useDatabase';
import { useAccountManagement } from '@grcs/hook/useAccountManagement';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { UserRole } from '@grcs/types/UserRole';
import { comparePassword, hashPassword } from '@grcs/utils/auth/password';
import {
  passwordRequirements,
  STAFF_PASSWORD_MIN_LENGTH,
} from '@grcs/utils/auth/staffPasswordPolicy';
import { PasswordField } from '@grcs/views/shared/components/PasswordField';
import { PasswordRequirements } from '@grcs/views/shared/components/PasswordRequirements';

/** Self-service password changes retain the legacy student credential path. */
export const AdminUpdatePasswordPageEnhanced: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localUser] = useLocalStorage('user', {});
  const userOps = useUserOperations();
  const accounts = useAccountManagement();
  const navigate = useNavigate();
  const toast = useToast();
  const surface = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.600');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const iconBackground = useColorModeValue('blue.50', 'blue.900');
  const isStudent = localUser.role === UserRole.STUDENT;
  const minimumLength = isStudent ? 8 : STAFF_PASSWORD_MIN_LENGTH;
  const dashboardPath = isStudent
    ? '/student'
    : localUser.role === UserRole.TEACHER
    ? '/teacher'
    : '/admin';
  const dashboardLabel = isStudent
    ? 'Student Dashboard'
    : localUser.role === UserRole.TEACHER
    ? 'Teacher Dashboard'
    : 'Admin Dashboard';
  const passwordsMismatch =
    !!confirmPassword && newPassword !== confirmPassword;
  // Derive validation directly so the button and checklist never lag behind typing.
  const isValid =
    passwordRequirements(newPassword, minimumLength, isStudent).every(
      (rule) => rule.isValid,
    ) &&
    !!confirmPassword &&
    !passwordsMismatch &&
    (!isStudent || !!currentPassword);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (isStudent) {
        // Student credentials are still stored as bcrypt hashes until their migration.
        const { data, error } = await userOps.getUserByUserToken(
          localUser.user_token,
        );
        if (error)
          throw new Error(
            'Unable to verify your current password. Please try again.',
          );
        if (!(await comparePassword(currentPassword, data?.[0]?.password))) {
          throw new Error('Current password is incorrect');
        }
        const hashedPassword = await hashPassword(newPassword);
        if (!hashedPassword)
          throw new Error('Unable to update your password. Please try again.');
        await userOps.updatePasswordByUserToken(
          localUser.user_token,
          hashedPassword,
        );
      } else if (
        localUser.role === UserRole.TEACHER ||
        localUser.role === UserRole.ADMIN
      ) {
        // The service verifies the Auth session and profile; browser role data is not authorization.
        await accounts.changeStaffPassword(newPassword);
      } else {
        throw new Error('Please sign in again before changing your password.');
      }
      toast({
        title: 'Password updated',
        description: 'Use your new password the next time you sign in.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate(dashboardPath);
    } catch (error: unknown) {
      toast({
        title: 'Password update failed',
        description:
          error instanceof Error
            ? error.message
            : 'Unable to update your password. Please try again.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout padding={4} maxWidth="760px">
      <VStack spacing={6} align="stretch">
        <Box>
          <Breadcrumb
            items={[
              { label: dashboardLabel, href: dashboardPath },
              { label: 'Update password', isCurrent: true },
            ]}
          />
          <Heading size="lg" mt={4}>
            Update password
          </Heading>
          <Text fontSize="sm" color={muted} mt={2}>
            Choose a password for your next sign-in.
          </Text>
        </Box>
        <Box
          as="form"
          onSubmit={handleSubmit}
          bg={surface}
          borderWidth="1px"
          borderColor={border}
          borderRadius="xl"
          overflow="hidden"
          aria-busy={isSubmitting}
        >
          <VStack align="stretch" spacing={5} p={{ base: 5, md: 7 }}>
            <HStack spacing={3}>
              <Box bg={iconBackground} p={3} borderRadius="lg">
                <FiLock aria-hidden="true" />
              </Box>
              <Box>
                <Heading size="sm">Your password</Heading>
                <Text fontSize="sm" color={muted} mt={1}>
                  Complete the requirements below to save.
                </Text>
              </Box>
            </HStack>
            {isStudent && (
              <PasswordField
                id="current-password"
                label="Current password"
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
                disabled={isSubmitting}
              />
            )}
            <PasswordField
              id="new-password"
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              disabled={isSubmitting}
            />
            <PasswordRequirements
              password={newPassword}
              minimumLength={minimumLength}
              requireComplexity={isStudent}
            />
            <PasswordField
              id="confirm-password"
              label="Confirm new password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              disabled={isSubmitting}
              error={passwordsMismatch ? 'Passwords do not match.' : undefined}
              helperText={
                confirmPassword && !passwordsMismatch
                  ? 'Passwords match.'
                  : undefined
              }
            />
          </VStack>
          <Stack
            direction={{ base: 'column-reverse', sm: 'row' }}
            justify="flex-end"
            spacing={3}
            p={{ base: 5, md: 6 }}
            borderTopWidth="1px"
            borderColor={border}
          >
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(dashboardPath)}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              leftIcon={<FiLock />}
              isDisabled={!isValid || isSubmitting}
              isLoading={isSubmitting}
              loadingText="Updating"
            >
              Update password
            </Button>
          </Stack>
        </Box>
      </VStack>
    </AdminLayout>
  );
};
